import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";

/**
 * Two round buttons in the lower right corner for long pages: back to the top once the page is scrolled, and to
 * the bottom while it is not there yet. Hidden when printing.
 */
export default function ScrollJumpButtons({ threshold = 400 }: { threshold?: number }): ReactElement | null {
  const { t } = useTranslation();
  const [nearTop, setNearTop] = useState(true);
  const [nearBottom, setNearBottom] = useState(true);

  useEffect(() => {
    const update = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setNearTop(y < threshold);
      setNearBottom(max - y < threshold);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    // The page grows and shrinks when lists open or close, without a scroll event.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(document.body);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [threshold]);

  if (nearTop && nearBottom) return null;

  const jump = (top: number) => {
    const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top, behavior: reduce ? "auto" : "smooth" });
  };
  const button = "inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-700 shadow-lg backdrop-blur hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-white/10 dark:bg-slate-900/95 dark:text-slate-200 dark:hover:bg-white/10";

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col gap-2 print:hidden">
      {!nearTop && (
        <button type="button" onClick={() => jump(0)} className={button}
          aria-label={t("common.to_top", { defaultValue: "To top" })} title={t("common.to_top", { defaultValue: "To top" })}>
          <FaArrowUp aria-hidden="true" />
        </button>
      )}
      {!nearBottom && (
        <button type="button" onClick={() => jump(document.documentElement.scrollHeight)} className={button}
          aria-label={t("common.to_bottom", { defaultValue: "To bottom" })} title={t("common.to_bottom", { defaultValue: "To bottom" })}>
          <FaArrowDown aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
